/**
 * Walkthrough registry — declarative, versioned tour content.
 *
 * Each tour anchors steps to real elements via `data-tour="<target>"`
 * attributes. Bump `version` after a material content change: users who
 * completed v1 will auto-see v2 (the seen-flag key includes the version).
 *
 * Copy edits = diffs to this file. See docs/PRD-in-app-walkthroughs.md.
 */

export interface TourStep {
  /** Matches `[data-tour="<target>"]` in the page. */
  target: string
  title: string
  body: string
  /**
   * What to do when the target isn't rendered (e.g. no forms yet):
   * 'center' shows the step as a centered card; 'skip' drops it.
   */
  fallback?: 'center' | 'skip'
}

export interface Tour {
  id: string
  /** Authed route this tour plays on (react-router pathname). */
  route: string
  /** Display name for the Account → Guides section. */
  title: string
  version: number
  steps: TourStep[]
}

const discoveryTour: Tour = {
  id: 'discovery',
  route: '/discovery',
  title: 'Discovery',
  version: 1,
  steps: [
    {
      target: 'discovery.templates',
      title: 'Pick a template',
      body: 'These chiclets set the shape of your form. quick = 3 fast questions, fields shows every field type, advanced is the full discovery.',
    },
    {
      target: 'discovery.audience',
      title: 'Who can answer',
      body: 'List the thread-party emails, comma-separated. Only these people can unlock the form — check spelling!',
    },
    {
      target: 'discovery.goal',
      title: 'Give it a goal',
      body: 'Optional, but a plain-English goal shapes the questions — the same way Ellis would draft it.',
    },
    {
      target: 'discovery.create',
      title: 'Draft, don’t send',
      body: 'This creates a draft. Nothing goes out until you activate it — you’re always the human in the loop.',
    },
    {
      target: 'discovery.form-card',
      title: 'Activate & share',
      body: 'Hit Activate on a draft to get a guest link. Send that link to your audience; Revoke kills it instantly. Click a card to watch answers land.',
      fallback: 'center',
    },
  ],
}

export const TOURS: Tour[] = [discoveryTour]

export function tourForRoute(pathname: string): Tour | undefined {
  return TOURS.find((t) => t.route === pathname)
}

export function tourById(id: string): Tour | undefined {
  return TOURS.find((t) => t.id === id)
}

/** Key used in the seen-map and localStorage suffix (versioned). */
export function tourVersionKey(tour: Tour): string {
  return `${tour.id}@v${tour.version}`
}
